import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  MeApi,
  MeApiKeys,
  useAllPreferences,
  type PreferencesListResponse,
} from '../api/me.api.js';

const DEBOUNCE_MS = 400;

/**
 * Per-user, per-key preference backed by `/api/me/preferences/:key`. The
 * whole prefs map is fetched once via `useAllPreferences`; individual reads
 * pull from that TanStack Query cache, writes are debounced and optimistically
 * patched.
 *
 * Returns `[value, setValue, isHydrated]`:
 *   - `value` is the default until the first fetch resolves; after that it
 *     reflects the stored value (still the default if no row exists).
 *   - `setValue(next)` writes through optimistically and persists after
 *     `DEBOUNCE_MS` of quiescence.
 *   - `isHydrated` lets callers avoid persisting their *defaults* back to the
 *     server on first render — only persist once we know what's stored.
 */
export function useUserPreference<T>(
  key: string,
  defaultValue: T,
): [T, (next: T) => void, boolean] {
  const queryClient = useQueryClient();
  const allPrefs = useAllPreferences();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingValue = useRef<T | null>(null);

  const value = useMemo<T>(() => {
    const row = allPrefs.data?.items.find((i) => i.key === key);
    return (row?.value as T) ?? defaultValue;
  }, [allPrefs.data, key, defaultValue]);

  const setValue = useCallback(
    (next: T) => {
      pendingValue.current = next;
      // Optimistically patch the cached prefs list so reads see the new value
      // immediately, without a network round-trip.
      queryClient.setQueryData<PreferencesListResponse | undefined>(
        [MeApiKeys.Preferences],
        (curr) => {
          const others = (curr?.items ?? []).filter((i) => i.key !== key);
          return {
            items: [
              { key, value: next as unknown, updatedAt: new Date().toISOString() },
              ...others,
            ],
          };
        },
      );
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        if (pendingValue.current !== null) {
          void MeApi.upsertPreference(key, pendingValue.current);
          pendingValue.current = null;
        }
      }, DEBOUNCE_MS);
    },
    [key, queryClient],
  );

  // Flush on unmount so a quick toggle then nav-away doesn't lose the write.
  useEffect(() => {
    return () => {
      if (timer.current && pendingValue.current !== null) {
        clearTimeout(timer.current);
        void MeApi.upsertPreference(key, pendingValue.current);
      }
    };
  }, [key]);

  return [value, setValue, allPrefs.isSuccess];
}
